import i18n from '../../../i18n'
/**
 * Lecture d'un fichier GPX pour l'import du planificateur : segments de tracé
 * (trkseg, ou rte à défaut) et POI (wpt).
 */

export interface GpxWaypoint {
  name: string
  lat: number
  lng: number
  description: string | null
}

export interface ParsedGpx {
  /** Un tableau [lng, lat][] par segment, dans l'ordre du fichier. */
  segments: [number, number][][]
  waypoints: GpxWaypoint[]
}

function childText(el: Element, tag: string): string | null {
  const text = el.getElementsByTagName(tag)[0]?.textContent?.trim()
  return text ? text : null
}

function readPoints(points: HTMLCollectionOf<Element>): [number, number][] {
  const coords: [number, number][] = []
  for (let i = 0; i < points.length; i++) {
    const lat = parseFloat(points[i].getAttribute('lat') ?? '')
    const lon = parseFloat(points[i].getAttribute('lon') ?? '')
    if (Number.isFinite(lat) && Number.isFinite(lon)) coords.push([lon, lat])
  }
  return coords
}

export function parseGpxForImport(xml: string): ParsedGpx {
  const doc = new DOMParser().parseFromString(xml, 'text/xml')
  if (doc.getElementsByTagName('parsererror').length > 0) {
    throw new Error(i18n.t('planning:parseGpxForImport.fichier_gpx_illisible'))
  }

  const segments: [number, number][][] = []
  const trksegs = doc.getElementsByTagName('trkseg')
  for (let i = 0; i < trksegs.length; i++) {
    const coords = readPoints(trksegs[i].getElementsByTagName('trkpt'))
    if (coords.length >= 2) segments.push(coords)
  }
  if (segments.length === 0) {
    const rtes = doc.getElementsByTagName('rte')
    for (let i = 0; i < rtes.length; i++) {
      const coords = readPoints(rtes[i].getElementsByTagName('rtept'))
      if (coords.length >= 2) segments.push(coords)
    }
  }

  const waypoints: GpxWaypoint[] = []
  const wpts = doc.getElementsByTagName('wpt')
  for (let i = 0; i < wpts.length; i++) {
    const wpt = wpts[i]
    const lat = parseFloat(wpt.getAttribute('lat') ?? '')
    const lng = parseFloat(wpt.getAttribute('lon') ?? '')
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue
    waypoints.push({
      name: childText(wpt, 'name') ?? `Point ${waypoints.length + 1}`,
      lat,
      lng,
      description: childText(wpt, 'desc') ?? childText(wpt, 'cmt'),
    })
  }

  return { segments, waypoints }
}

export async function parseGpxFileForImport(file: File): Promise<ParsedGpx> {
  return parseGpxForImport(await file.text())
}
